import { ActivityKindEnum, RobotActivityItemOutDTO } from '@api/ringil-api.v2';

/**
 * One run of the robot over one entity: the queue rows either side of it and every rule it put to
 * that entity, as a single thing to read.
 *
 * The flat feed interleaves rows from different expeditions — a queued row here, three rule rows
 * there, a finished row somewhere below — so following what actually happened to one expedition
 * means reading past everything that happened to the others. Grouped, each card answers the
 * question a dispatcher is actually asking: *what did the robot do about this shipment?*
 */
export interface FeedGroup {
  /** Stable across polls: the id of the newest row in the group. */
  id: string;
  /** The newest row's time — the group sorts and dates by it. */
  at: string;
  entityType?: RobotActivityItemOutDTO['entityType'];
  entityId?: string;
  entityLabel?: string;
  triggerType?: string;
  /** Rule rows, newest first, with the no-match ones left in — the UI folds those away itself. */
  rules: RobotActivityItemOutDTO[];
  actedCount: number;
  failedCount: number;
  skippedCount: number;
  /** How many rules the event said it would consider; only the queue row knows. */
  ruleCount?: number;
  /** In the future on a delayed rule — the answer to "why hasn't it done anything yet". */
  notBefore?: string;
  queued: boolean;
  finished: boolean;
  /** An event-level failure: the processing broke, not one rule inside it. */
  error?: string;
}

/** How this group reads at a glance, worst news first. */
export type GroupState = 'FAILED' | 'ACTED' | 'RUNNING' | 'NO_MATCH' | 'DONE';

export const groupState = (group: FeedGroup): GroupState => {
  if (group.failedCount > 0 || group.error) return 'FAILED';
  if (group.actedCount > 0) return 'ACTED';
  // Queued and not yet finished — either waiting on a delay or being worked on right now.
  if (group.queued && !group.finished) return 'RUNNING';
  if (group.skippedCount > 0) return 'NO_MATCH';
  return 'DONE';
};

/**
 * Rows more than this far apart are two different runs even on the same entity.
 *
 * The feed carries no event id — a rule row names its execution, a queue row names its event — so
 * the grouping is inferred, and this is the safety net for an expedition the robot touches twice.
 * Generous, because a single run can span a rule with a delay on it.
 */
const MAX_GROUP_SPAN_MS = 10 * 60 * 1000;

const isRuleRow = (item: RobotActivityItemOutDTO) =>
  item.activityKind === ActivityKindEnum.RULE_MATCHED || item.activityKind === ActivityKindEnum.RULE_SKIPPED || item.activityKind === ActivityKindEnum.RULE_FAILED;

/**
 * Folds the flat feed into one group per run, newest first.
 *
 * Walks newest → oldest, keeping one open group per entity. An `EVENT_QUEUED` row is where a run
 * *starts*, so reaching one closes that entity's group: anything older belongs to an earlier run.
 * A change of trigger or a gap wider than [MAX_GROUP_SPAN_MS] closes it too.
 *
 * @param items the feed, already sorted newest first.
 */
export const groupFeedItems = (items: RobotActivityItemOutDTO[]): FeedGroup[] => {
  const groups: FeedGroup[] = [];
  /** The group still accepting rows for a given entity, if any. */
  const open = new Map<string, FeedGroup>();

  items.forEach(item => {
    const key = item.entityId ?? item.id;
    let group = open.get(key);

    const startsNewGroup =
      !group ||
      (item.triggerType && group.triggerType && item.triggerType !== group.triggerType) ||
      new Date(group.at).getTime() - new Date(item.at).getTime() > MAX_GROUP_SPAN_MS;

    if (startsNewGroup) {
      group = {
        id: item.id,
        at: item.at,
        entityType: item.entityType,
        entityId: item.entityId,
        entityLabel: item.entityLabel,
        triggerType: item.triggerType,
        rules: [],
        actedCount: 0,
        failedCount: 0,
        skippedCount: 0,
        queued: false,
        finished: false,
      };
      groups.push(group);
      open.set(key, group);
    }

    // The group takes its identity from the newest row it has, and its detail from whichever row
    // happens to know a given thing — only the queue rows carry `ruleCount` and `notBefore`.
    group.triggerType = group.triggerType ?? item.triggerType;
    group.entityLabel = group.entityLabel ?? item.entityLabel;
    group.ruleCount = group.ruleCount ?? item.ruleCount ?? undefined;
    group.notBefore = group.notBefore ?? item.notBefore ?? undefined;

    if (isRuleRow(item)) {
      group.rules.push(item);
      if (item.activityKind === ActivityKindEnum.RULE_MATCHED) group.actedCount += 1;
      if (item.activityKind === ActivityKindEnum.RULE_FAILED) group.failedCount += 1;
      if (item.activityKind === ActivityKindEnum.RULE_SKIPPED) group.skippedCount += 1;
    }

    if (item.activityKind === ActivityKindEnum.EVENT_FINISHED) group.finished = true;
    if (item.activityKind === ActivityKindEnum.EVENT_FAILED) {
      group.error = group.error ?? item.error;
      group.failedCount += 1;
    }
    if (item.activityKind === ActivityKindEnum.EVENT_QUEUED) {
      group.queued = true;
      // A run starts here, so nothing older joins this group.
      open.delete(key);
    }

    group.error = group.error ?? item.error ?? undefined;
  });

  return groups;
};
